import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useFetch } from "../api";
import { Alerts, Badge, EmptyRow, Field, Loading, Page, Pager, Tabs, UserLink } from "../components/ui";
import { label, money, PAGE_SIZE, percent, qs, setQuery, when } from "../format";

export default function Purchases() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const status = params.get("status") || "";
  const userId = params.get("userId") || "";
  const [draft, setDraft] = useState(userId);
  const list = useFetch("/purchases" + qs({ page, limit: PAGE_SIZE, status, userId }));
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];

  useEffect(() => setDraft(userId), [userId]);

  return (
    <Page title="Purchases" text="Read only. Members buy packages from the single wallet. The rate on each row is locked to that purchase.">
      <Alerts error={list.error} />
      <form className="toolbar" onSubmit={(event) => {
        event.preventDefault();
        setQuery(params, setParams, { userId: draft.trim(), page: "" });
      }}>
        <Field label="Member id" error={list.errors.userId}>
          <input value={draft} onChange={(event) => setDraft(event.target.value)} />
        </Field>
        <button className="btn small">Filter</button>
        {userId ? (
          <button type="button" className="btn ghost small" onClick={() => setQuery(params, setParams, { userId: "", page: "" })}>Clear</button>
        ) : null}
      </form>
      <Tabs
        value={status}
        onChange={(value) => setQuery(params, setParams, { status: value, page: "" })}
        options={[
          { value: "", label: "All" },
          { value: "active", label: "Active" },
          { value: "completed", label: "Completed" },
        ]}
      />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table className="wide">
            <thead>
              <tr>
                <th>Member</th><th>Package</th><th className="num">Paid</th><th>Rate</th><th className="num">Per day</th>
                <th>Days</th><th className="num">Earned</th><th>Status</th><th>Start</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id} className="click" onClick={() => navigate("/admin/purchases/" + row.id)}>
                  <td onClick={(event) => event.stopPropagation()}><UserLink user={row.user} /></td>
                  <td>{row.packageName}<div className="code">{row.packageCode}</div></td>
                  <td className="num">{money(row.amount)}</td>
                  <td>{percent(row.dailyRatePercent)}</td>
                  <td className="num">{money(row.dailyEarning)}</td>
                  <td>{row.creditedDays}/{row.durationDays}</td>
                  <td className="num">{money(row.earnedAmount)}</td>
                  <td><Badge value={row.status} /></td>
                  <td>{when(row.startDate)}</td>
                </tr>
              )) : <EmptyRow cols={9} text="No purchases" />}
            </tbody>
          </table>
        </div>
      )}
      {list.data ? (
        <Pager page={page} limit={PAGE_SIZE} total={list.data.total} onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })} />
      ) : null}
    </Page>
  );
}

export function PurchaseDetail() {
  const { id } = useParams();
  const { data, loading, error } = useFetch("/purchases/" + id);
  const row = data?.data;
  const schedule = row?.schedule || [];

  return (
    <Page title="Purchase">
      <Link className="back" to="/admin/purchases">Back to purchases</Link>
      <Alerts error={error} />
      {loading ? <Loading /> : null}
      {row ? (
        <>
          <div className="panel">
            <h2 className="section-title">{row.packageName}</h2>
            <div className="facts">
              <div><dt>Member</dt><dd><UserLink user={row.user} /></dd></div>
              <div><dt>Code</dt><dd className="code">{row.packageCode}</dd></div>
              <div><dt>Paid</dt><dd>{money(row.amount)}</dd></div>
              <div><dt>Daily rate</dt><dd>{percent(row.dailyRatePercent)}</dd></div>
              <div><dt>Per day</dt><dd>{money(row.dailyEarning)}</dd></div>
              <div><dt>Days credited</dt><dd>{row.creditedDays} / {row.durationDays}</dd></div>
              <div><dt>Earned</dt><dd>{money(row.earnedAmount)}</dd></div>
              <div><dt>Expected total</dt><dd>{money(row.expectedTotal)}</dd></div>
              <div><dt>Principal</dt><dd>{label(row.principalMode)}</dd></div>
              <div><dt>Status</dt><dd><Badge value={row.status} /></dd></div>
              <div><dt>Start</dt><dd>{when(row.startDate)}</dd></div>
              <div><dt>Created</dt><dd>{when(row.createdAt)}</dd></div>
            </div>
          </div>
          <h2 className="section-title">Schedule</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Day</th><th>Due</th><th className="num">Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {schedule.length ? schedule.map((day) => (
                  <tr key={day.day}>
                    <td>{day.day}</td>
                    <td>{when(day.dueDate)}</td>
                    <td className="num">{money(day.amount)}</td>
                    <td><Badge value={day.status} /></td>
                  </tr>
                )) : <EmptyRow cols={4} text="No schedule" />}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Page>
  );
}
